// Standalone dispatch „otevři detail objektu dle ObjectCategory + ID" — bez React kontextu
// bridge (volá se z `MenuNavigationShellPage`). Magic generický `form/edit` handler přeloží
// ObjectCategory na editační formulář a probublá zpět jako `form/open` → bridge otevře agendu
// (respektuje `pendingMenuOpen` pro replace/detached). Cílí na Magic desktop přes `getMagicDesktopFrame()`.

import { useCallback } from 'react';
import { getMagicDesktopFrame, setPendingLegacyNavSource } from '@/shared/lib/legacyMagic';
import { useCurrentLayerId } from '@/shared/lib/layerReload';
import { openObjectCategoryDetail } from '@/shared/lib/objectCategoryNav';
import { PHOENIX_TO_MAGIC_ACTIONS } from './types';

/**
 * Otevři detail agendy pro `objectCategory` (např. `BusinessCase`) s daným `id`.
 *
 * Postupná náhrada legacy → phoenix: je-li pro `objectCategory` zaregistrovaná native agenda
 * (viz `@/shared/lib/objectCategoryNav`), otevře se native detail a s Magicem se nekomunikuje.
 * Jinak fallback na legacy — Magic si sám dohledá editační formulář i parametr `ID`.
 *
 * `sourceLayerId` = vrstva, ze které se otevírá (`null` = plocha/menu/našeptávač). Native i legacy
 * cesta z něj zdědí floating stack, takže detail otevřený z OSAMOSTATNĚNÉHO okna zůstane v jeho
 * stacku místo na ploše. Uvnitř agendy volej rovnou {@link useMagicFormEdit}, který ho doplní sám.
 */
export function sendMagicFormEdit(
  objectCategory: string,
  id: number,
  sourceLayerId: string | null = null
): void {
  if (openObjectCategoryDetail(objectCategory, id, sourceLayerId)) return;
  const frame = getMagicDesktopFrame();
  if (!frame) return;
  // Zdroj předáváme bokem (consume-once): `form/open` se vrátí ze skrytého control iframu, který
  // okno neidentifikuje, takže bridge by jinak vrstvu otevřel na ploše.
  setPendingLegacyNavSource(sourceLayerId);
  frame.postMessage(
    {
      action: PHOENIX_TO_MAGIC_ACTIONS.formEdit,
      data: { ObjectCategory: objectCategory, ID: id },
      requestId: crypto.randomUUID(),
    },
    '*'
  );
}

/**
 * React varianta {@link sendMagicFormEdit} navázaná na aktuální vrstvu (`useCurrentLayerId`).
 * Uvnitř agendy ji preferuj před holým `sendMagicFormEdit` — jinak se detail otevře na ploše
 * i tehdy, když se prokliká z osamostatněného okna.
 */
export function useMagicFormEdit(): (objectCategory: string, id: number) => void {
  const sourceLayerId = useCurrentLayerId();
  return useCallback(
    (objectCategory: string, id: number) => {
      sendMagicFormEdit(objectCategory, id, sourceLayerId);
    },
    [sourceLayerId]
  );
}
